import axiosInstance from '@/app/api/axiosInstance';
import { Party, PartyListResponse, LastOrderInfo, PartyNoOrderSummary, PartyWithLastOrderListResponse, PartyUnpaidInvoiceSummary, PartyWithUnpaidInfoListResponse, PartyUser, ClientUserResponse, ResetUserPinResponse } from '@/app/types/party';

/**
 * List parties with pagination and filtering
 * Filter by primary_party_type=Customer to show only customers
 * If responsiblePartyId is provided, filter by responsible_party_id
 */
export const getParties = async (
    limit: number = 50,
    offset: number = 0,
    searchQuery?: string,
    responsiblePartyId?: number
): Promise<PartyListResponse> => {
    const filters: Array<{ field: string; op: string; value: string | number }> = [
        { field: 'primary_party_type', op: 'eq', value: 'Customer' }
    ];

    // If user has a managed party, filter by responsible_party_id
    if (responsiblePartyId) {
        filters.push({ field: 'responsible_party_id', op: 'eq', value: responsiblePartyId });
    }

    const params: Record<string, unknown> = {
        limit,
        skip: offset,
        sort_by: 'name',
        sort_dir: 'asc',
        filters: JSON.stringify(filters)
    };

    if (searchQuery) {
        params.search_query = searchQuery;
    }

    const response = await axiosInstance.get<PartyListResponse>('/parties/', { params });
    return response.data;
};

/**
 * Get a single party by ID
 */
export const getPartyById = async (partyId: number): Promise<Party> => {
    const response = await axiosInstance.get<Party>(`/parties/${partyId}`);
    return response.data;
};

/**
 * Update a party's geo_location (a Google Maps URL or location link).
 * Uses the partial-update PATCH endpoint so only geo_location is changed.
 */
export const updatePartyLocation = async (
    partyId: number,
    geoLocation: string
): Promise<Party> => {
    const response = await axiosInstance.patch<Party>(
        `/parties/${partyId}`,
        { geo_location: geoLocation }
    );
    return response.data;
};

/**
 * Get recent sales invoices for a party
 * Uses the vouchers endpoint with party_id and voucher_type_id filters
 * Must pass siteIds to search across all user's accessible sites
 */
export const getRecentOrdersForParty = async (
    partyId: number,
    siteIds: number[],
    limit: number = 5
): Promise<LastOrderInfo[]> => {
    try {
        // Build filters for party_id, voucher_type (Sales Invoice = 1), and sites
        const filters: Array<{ field: string; op: string; value: number | number[] }> = [
            { field: 'party_id', op: 'eq', value: partyId },
            { field: 'voucher_type_id', op: 'eq', value: 1 }, // Sales Invoice
        ];

        // Add site filter to search across all user's sites
        if (siteIds.length > 0) {
            filters.push({ field: 'site_id', op: 'in', value: siteIds });
        }

        const params = {
            limit,
            sort_by: 'transaction_date',
            sort_dir: 'desc',
            filters: JSON.stringify(filters),
        };

        const response = await axiosInstance.get('/vouchers/', { params });
        const vouchers = response.data.items || [];

        return vouchers.map((voucher: {
            id: number;
            voucher_number: string;
            transaction_date: string;
            total_amount: string;
            payment_status: string | null;
            amount_due: string | null;
        }) => ({
            id: voucher.id,
            voucher_number: voucher.voucher_number,
            transaction_date: voucher.transaction_date,
            total_amount: voucher.total_amount,
            payment_status: voucher.payment_status,
            amount_due: voucher.amount_due,
        }));
    } catch (error) {
        console.error('Failed to fetch recent orders for party:', error);
        return [];
    }
};

/**
 * Get summary of parties with no orders by time buckets
 * Used for the manager dashboard to show counts of inactive customers
 *
 * @param responsiblePartyId - Filter by manager's assigned parties
 * @param buckets - Optional custom day buckets (default: "15,30,60,90")
 */
export const getPartiesNoOrderSummary = async (
    responsiblePartyId?: number,
    buckets?: string,
    exclusive: boolean = false
): Promise<PartyNoOrderSummary> => {
    const params: Record<string, unknown> = {
        party_type: 'Customer'
    };

    if (responsiblePartyId) {
        params.responsible_party_id = responsiblePartyId;
    }

    if (buckets) {
        params.buckets = buckets;
    }

    if (exclusive) {
        params.exclusive = true;
    }

    const response = await axiosInstance.get<PartyNoOrderSummary>(
        '/parties/no-order-summary',
        { params }
    );
    return response.data;
};

/**
 * Get parties filtered by no-order period
 * Used when clicking on a dashboard bucket to see the detailed list
 *
 * @param noOrderDays - Filter parties with no orders in the last N days
 * @param limit - Number of results per page
 * @param offset - Pagination offset
 * @param responsiblePartyId - Filter by manager's assigned parties
 * @param searchQuery - Optional search query
 * @param includeNeverOrdered - Include parties that have never ordered (default: true)
 */
export const getPartiesWithNoOrders = async (
    noOrderDays: number,
    limit: number = 50,
    offset: number = 0,
    responsiblePartyId?: number,
    searchQuery?: string,
    includeNeverOrdered: boolean = true,
    noOrderDaysMax?: number
): Promise<PartyWithLastOrderListResponse> => {
    const filters: Array<{ field: string; op: string; value: string | number }> = [
        { field: 'primary_party_type', op: 'eq', value: 'Customer' }
    ];

    if (responsiblePartyId) {
        filters.push({ field: 'responsible_party_id', op: 'eq', value: responsiblePartyId });
    }

    const params: Record<string, unknown> = {
        limit,
        skip: offset,
        sort_by: 'last_order_date',
        sort_dir: 'asc',  // Show oldest first (longest without order)
        filters: JSON.stringify(filters),
        no_order_days: noOrderDays,
        include_never_ordered: includeNeverOrdered,
        include_last_order_date: true
    };

    if (noOrderDaysMax !== undefined) {
        params.no_order_days_max = noOrderDaysMax;
    }

    if (searchQuery) {
        params.search_query = searchQuery;
    }

    const response = await axiosInstance.get<PartyWithLastOrderListResponse>(
        '/parties/',
        { params }
    );
    return response.data;
};

/**
 * Get summary of parties with unpaid invoices by age buckets
 */
export const getPartiesUnpaidInvoiceSummary = async (
    responsiblePartyId?: number,
    buckets?: string,
): Promise<PartyUnpaidInvoiceSummary> => {
    const params: Record<string, unknown> = {
        party_type: 'Customer'
    };

    if (responsiblePartyId) {
        params.responsible_party_id = responsiblePartyId;
    }

    if (buckets) {
        params.buckets = buckets;
    }

    const response = await axiosInstance.get<PartyUnpaidInvoiceSummary>(
        '/parties/unpaid-invoice-summary',
        { params }
    );
    return response.data;
};

/**
 * Get parties with unpaid invoices filtered by invoice age range
 */
export const getPartiesWithUnpaidInvoices = async (
    unpaidInvoiceDays: number,
    limit: number = 50,
    offset: number = 0,
    responsiblePartyId?: number,
    searchQuery?: string,
    unpaidInvoiceDaysMax?: number
): Promise<PartyWithUnpaidInfoListResponse> => {
    const filters: Array<{ field: string; op: string; value: string | number }> = [
        { field: 'primary_party_type', op: 'eq', value: 'Customer' }
    ];

    if (responsiblePartyId) {
        filters.push({ field: 'responsible_party_id', op: 'eq', value: responsiblePartyId });
    }

    const params: Record<string, unknown> = {
        limit,
        skip: offset,
        sort_by: 'total_amount_due',
        sort_dir: 'desc',
        filters: JSON.stringify(filters),
        unpaid_invoice_days: unpaidInvoiceDays,
    };

    if (unpaidInvoiceDaysMax !== undefined) {
        params.unpaid_invoice_days_max = unpaidInvoiceDaysMax;
    }

    if (searchQuery) {
        params.search_query = searchQuery;
    }

    const response = await axiosInstance.get<PartyWithUnpaidInfoListResponse>(
        '/parties/',
        { params }
    );
    return response.data;
};

/**
 * List the user accounts linked to a party (backend: `party.managing_users`).
 *
 * This is how the PWA decides between "create app login" and "reset PIN" — the
 * party payload itself carries no client-user field (`client_user_id` on the
 * desktop app's Party type is a deprecated FK the API no longer returns).
 * Requires org_admin.
 */
export const getPartyUsers = async (partyId: number): Promise<PartyUser[]> => {
    const response = await axiosInstance.get<PartyUser[]>(`/parties/${partyId}/users`);
    return response.data;
};

/**
 * Create a client (customer app) login for a party. The backend derives the
 * username from the party's phone number and returns a generated 4-digit PIN in
 * plain text — this is the only time the PIN is ever readable.
 *
 * Fails with 400 if the party has no phone, and 409 if that phone is already a
 * username on a non-client account. Requires org_admin.
 */
export const createClientUser = async (partyId: number): Promise<ClientUserResponse> => {
    const response = await axiosInstance.post<ClientUserResponse>(
        `/parties/${partyId}/create-client-user`
    );
    return response.data;
};

/**
 * Reset a client user's PIN and return the new one in plain text.
 *
 * Targets an explicit user rather than POST /parties/{id}/reset-client-pin,
 * which silently resets whichever linked user happens to come first — parties
 * can have several. Requires org_admin.
 */
export const resetUserPin = async (userId: number): Promise<ResetUserPinResponse> => {
    const response = await axiosInstance.post<ResetUserPinResponse>(
        `/users/${userId}/reset-pin`
    );
    return response.data;
};
